import React from "react";

export default function Button({
    onClick,
    text,
    id,
    type = "outlined",
    disabled = false,
    loading = false,
    icon,
    className,
    height,
    actionType = "button",
    iconPosition = "right",
    loadingText,
    backgroundColor,
    textColor,
}: {
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    text: string;
    id: string;
    type?: "outlined" | "filled";
    disabled?: boolean;
    loading?: boolean;
    icon?: React.ReactNode;
    className?: string;
    height?: string;
    actionType?: "submit" | "button";
    iconPosition?: "left" | "right";
    loadingText?: string;
    backgroundColor?: string;
    textColor?: string;
}) {
    // Helper function to check if a string is a color value (hex, rgb, etc.) or a basic CSS color name
    const isColorValue = (str: string) => {
        // Check for hex, rgb, hsl formats
        if (/^(#|rgb|hsl|rgba|hsla)/.test(str)) {
            return true;
        }

        // Common CSS color names that might also be Tailwind class names
        const commonColorNames = [
            "black",
            "white",
            "red",
            "green",
            "blue",
            "yellow",
            "purple",
            "pink",
            "gray",
            "brown",
            "orange",
            "cyan",
            "magenta",
            "lime",
            "olive",
            "navy",
            "teal",
            "aqua",
            "silver",
            "gold",
        ];

        return commonColorNames.includes(str.toLowerCase());
    };

    // Determine if backgroundColor and textColor are Tailwind classes or actual color values
    const bgIsTailwind = backgroundColor && !isColorValue(backgroundColor);
    const textIsTailwind = textColor && !isColorValue(textColor);

    // Set up Tailwind classes for the button type
    const buttonType =
        disabled || loading
            ? `bg-gray-300 border-gray-300 text-rthm-dark-blue dark:bg-gray-600 dark:border-gray-600 dark:text-gray-200`
            : type === "outlined"
              ? `border-cyan-800 text-cyan-800 dark:border-white dark:text-white`
              : bgIsTailwind && textIsTailwind
                ? `bg-${backgroundColor} text-${textColor}`
                : `bg-cyan-800 text-white dark:bg-cyan-700 border-none`;

    // Set up inline styles for non-Tailwind color values
    const customStyles =
        backgroundColor &&
        textColor &&
        !disabled &&
        !loading &&
        type !== "outlined" &&
        (!bgIsTailwind || !textIsTailwind)
            ? {
                  backgroundColor: !bgIsTailwind ? backgroundColor : undefined,
                  color: !textIsTailwind ? textColor : undefined,
              }
            : {};

    const spinnerSize = "h-6 w-6";
    const buttonHeight = height || "h-fit";

    // Dark mode transition
    const transitionClass = "transition-colors duration-200";

    const renderSpinner = () => (
        <div
            data-testid="loading-spinner"
            className={`animate-spin rounded-full ${spinnerSize} border-b-2 border-white dark:border-gray-200 flex items-center justify-center`}
        ></div>
    );

    const iconWrapperClass = `flex items-center justify-center ${iconPosition === "left" ? "mr-2" : "ml-2"}`;
    const textClass = `leading-[14.30px] font-[599] font-satoshi tracking-wide text-base`;
    const renderLoadingText = () => (
        <div className={`inline-flex items-center ${textClass}`}>
            {loadingText}
            <span className="ellipsis-animation"></span>
        </div>
    );

    const renderButtonContent = () => (
        <div className="flex flex-row items-center justify-center">
            {loading ? (
                loadingText ? (
                    renderLoadingText()
                ) : (
                    renderSpinner()
                )
            ) : (
                <>
                    {icon && iconPosition === "left" && (
                        <div className={iconWrapperClass}>{icon}</div>
                    )}
                    <div className={textClass}>{text}</div>
                    {icon && iconPosition === "right" && (
                        <div className={iconWrapperClass}>{icon}</div>
                    )}
                </>
            )}
        </div>
    );
    return (
        <button
            id={id}
            type={actionType}
            onClick={onClick}
            disabled={disabled || loading}
            className={`py-2.5 min-h-12 px-4 ${buttonHeight} rounded-full border dark:border-1 flex justify-center items-center ${buttonType} ${disabled || loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${transitionClass} ${className}`}
            style={customStyles}
        >
            {renderButtonContent()}
        </button>
    );
}
